import { useCallback, useEffect, useState } from "react";
import {
  BarChart3,
  Clock3,
  Coins,
  RefreshCw,
  ShoppingBag,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { api, ApiRequestError } from "../lib/api";
import type { OwnerOutlet, ReportResponse } from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { formatRupiah } from "../lib/format";

export function ReportsPage() {
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [outlets, setOutlets] = useState<OwnerOutlet[]>([]);
  const [outletId, setOutletId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, outletResponse] = await Promise.all([
        api<ReportResponse>(
          `/api/owner/reports/today${outletId ? `?outletId=${encodeURIComponent(outletId)}` : ""}`,
        ),
        api<{ outlets: OwnerOutlet[] }>("/api/owner/outlets"),
      ]);
      setReport(data);
      setOutlets(outletResponse.outlets);
      if (!outletId && outletResponse.outlets[0]) {
        setOutletId(outletResponse.outlets[0].id);
      }
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat laporan.",
      );
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    void load();
  }, [load]);

  const maxHourly = Math.max(
    1,
    ...(report?.hourly.map((entry) => entry.revenue) ?? [1]),
  );

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Hari ini"
        title="Laporan penjualan"
        description="Omzet, pesanan, dan menu terlaris. Angka dihitung dari pesanan yang sudah dibayar."
        actions={
          <div className="kds-actions">
            <select
              className="input outlet-select"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
            >
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary" onClick={() => void load()}>
              <RefreshCw size={16} /> Muat ulang
            </button>
          </div>
        }
      />

      {error ? <div className="alert alert--danger">{error}</div> : null}

      {loading || !report ? (
        <div className="empty-state empty-state--roomy">
          <BarChart3 size={32} />
          <h3>Memuat laporan...</h3>
        </div>
      ) : (
        <>
          <section className="stat-grid stat-grid--reports">
            <article className="stat-card">
              <span className="stat-card__icon stat-card__icon--accent"><Wallet size={18} /></span>
              <div>
                <p className="stat-card__label">Omzet</p>
                <strong className="stat-card__value">{formatRupiah(report.summary.revenue)}</strong>
                <span className="stat-card__hint">{report.summary.paidOrders} pesanan dibayar</span>
              </div>
            </article>
            <article className="stat-card">
              <span className="stat-card__icon"><ShoppingBag size={18} /></span>
              <div>
                <p className="stat-card__label">Rata-rata / pesanan</p>
                <strong className="stat-card__value">{formatRupiah(report.summary.averageOrderValue)}</strong>
                <span className="stat-card__hint">AOV hari ini</span>
              </div>
            </article>
            <article className="stat-card">
              <span className="stat-card__icon"><Clock3 size={18} /></span>
              <div>
                <p className="stat-card__label">Antrean aktif</p>
                <strong className="stat-card__value">{report.summary.activeOrders}</strong>
                <span className="stat-card__hint">{report.summary.awaitingPayment} menunggu bayar</span>
              </div>
            </article>
            <article className="stat-card">
              <span className="stat-card__icon"><Coins size={18} /></span>
              <div>
                <p className="stat-card__label">Dibatalkan</p>
                <strong className="stat-card__value">{report.summary.cancelledOrders}</strong>
                <span className="stat-card__hint">Hari ini</span>
              </div>
            </article>
          </section>

          <div className="report-grid">
            <section className="card card--pad">
              <header className="report-card__head">
                <div>
                  <p className="eyebrow">Per jam</p>
                  <h2>Distribusi omzet</h2>
                </div>
                <TrendingUp size={20} />
              </header>
              {report.hourly.length === 0 ? (
                <p className="menu-admin__empty">Belum ada penjualan hari ini.</p>
              ) : (
                <div className="report-bars">
                  {report.hourly.map((entry) => (
                    <div className="report-bar" key={entry.hour}>
                      <span className="report-bar__label">{String(entry.hour).padStart(2, "0")}:00</span>
                      <div className="report-bar__track">
                        <span
                          className="report-bar__fill"
                          style={{ width: `${Math.max(4, (entry.revenue / maxHourly) * 100)}%` }}
                        />
                      </div>
                      <span className="report-bar__value">{formatRupiah(entry.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="card card--pad">
              <header className="report-card__head">
                <div>
                  <p className="eyebrow">Menu</p>
                  <h2>Terlaris hari ini</h2>
                </div>
                <BarChart3 size={20} />
              </header>
              {report.topItems.length === 0 ? (
                <p className="menu-admin__empty">Belum ada item terjual.</p>
              ) : (
                <ol className="top-items">
                  {report.topItems.map((item, index) => (
                    <li key={item.name}>
                      <span className="top-items__rank">{index + 1}</span>
                      <div>
                        <strong>{item.name}</strong>
                        <span>{item.qty} terjual</span>
                      </div>
                      <span>{formatRupiah(item.revenue)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
